'use client';

import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { Button, Field, Input, Modal, Textarea } from '@/ui';

type ConfirmDialogOptions = {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: 'primary' | 'danger' | 'danger-outline';
};

type InputDialogOptions = {
  title: string;
  label: string;
  description?: string;
  placeholder?: string;
  initialValue?: string;
  submitLabel?: string;
  multiline?: boolean;
  rows?: number;
  allowEmpty?: boolean;
  inputType?: string;
  renderInput?: (props: { value: string; onChange: (value: string) => void }) => React.ReactNode;
};

type ConfirmDialogState = ConfirmDialogOptions & {
  open: boolean;
};

type InputDialogState = InputDialogOptions & {
  open: boolean;
  value: string;
};

type DialogContextValue = {
  confirm: (options: ConfirmDialogOptions) => Promise<boolean>;
  requestInput: (options: InputDialogOptions) => Promise<string | null>;
};

const DialogContext = createContext<DialogContextValue | null>(null);

const DEFAULT_CONFIRM_STATE: ConfirmDialogState = {
  open: false,
  title: '',
  message: '',
};

const DEFAULT_INPUT_STATE: InputDialogState = {
  open: false,
  title: '',
  label: '',
  description: '',
  placeholder: '',
  submitLabel: 'Save',
  multiline: false,
  rows: 4,
  allowEmpty: false,
  inputType: 'text',
  value: '',
};

export function DialogProvider({ children }: { children: ReactNode }) {
  const confirmResolverRef = useRef<((value: boolean) => void) | null>(null);
  const inputResolverRef = useRef<((value: string | null) => void) | null>(null);
  const [confirmState, setConfirmState] = useState<ConfirmDialogState>(DEFAULT_CONFIRM_STATE);
  const [inputState, setInputState] = useState<InputDialogState>(DEFAULT_INPUT_STATE);

  const confirm = useCallback((options: ConfirmDialogOptions): Promise<boolean> => {
    setConfirmState({
      open: true,
      ...options,
    });
    return new Promise<boolean>((resolve) => {
      confirmResolverRef.current = resolve;
    });
  }, []);

  const requestInput = useCallback((options: InputDialogOptions): Promise<string | null> => {
    setInputState({
      open: true,
      title: options.title,
      label: options.label,
      description: options.description,
      placeholder: options.placeholder,
      submitLabel: options.submitLabel || 'Save',
      multiline: options.multiline || false,
      rows: options.rows || 4,
      allowEmpty: options.allowEmpty || false,
      inputType: options.inputType || 'text',
      renderInput: options.renderInput,
      value: options.initialValue || '',
    });
    return new Promise<string | null>((resolve) => {
      inputResolverRef.current = resolve;
    });
  }, []);

  const closeConfirm = useCallback((result: boolean) => {
    confirmResolverRef.current?.(result);
    confirmResolverRef.current = null;
    setConfirmState(DEFAULT_CONFIRM_STATE);
  }, []);

  const closeInput = useCallback((result: string | null) => {
    inputResolverRef.current?.(result);
    inputResolverRef.current = null;
    setInputState(DEFAULT_INPUT_STATE);
  }, []);

  const submitDisabled = !inputState.allowEmpty && inputState.value.trim() === '';

  return (
    <DialogContext.Provider value={{ confirm, requestInput }}>
      {children}
      <Modal
        open={confirmState.open}
        onClose={() => closeConfirm(false)}
        title={confirmState.title}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => closeConfirm(false)}>
              {confirmState.cancelLabel ?? 'Cancel'}
            </Button>
            <Button
              variant={confirmState.confirmVariant === 'primary' ? 'primary' : 'danger'}
              onClick={() => closeConfirm(true)}
            >
              {confirmState.confirmLabel ?? 'Confirm'}
            </Button>
          </>
        }
      >
        <p className="whitespace-pre-line text-sm text-zinc-300">{confirmState.message}</p>
      </Modal>
      <Modal
        open={inputState.open}
        onClose={() => closeInput(null)}
        title={inputState.title}
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={() => closeInput(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={submitDisabled}
              onClick={() => closeInput(inputState.value)}
            >
              {inputState.submitLabel || 'Save'}
            </Button>
          </>
        }
      >
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!submitDisabled) closeInput(inputState.value);
          }}
        >
          <Field label={inputState.label} hint={inputState.description || undefined}>
            {inputState.renderInput ? (
              inputState.renderInput({
                value: inputState.value,
                onChange: (value) => setInputState((current) => ({ ...current, value })),
              })
            ) : inputState.multiline ? (
              <Textarea
                value={inputState.value}
                rows={inputState.rows}
                placeholder={inputState.placeholder}
                onChange={(e) => setInputState((current) => ({ ...current, value: e.target.value }))}
                autoFocus
              />
            ) : (
              <Input
                type={inputState.inputType}
                value={inputState.value}
                placeholder={inputState.placeholder}
                // Shared/action passwords (not the user's login) — keep the
                // browser's save-password prompt out of it.
                autoComplete={inputState.inputType === 'password' ? 'new-password' : undefined}
                onChange={(e) => setInputState((current) => ({ ...current, value: e.target.value }))}
                autoFocus
              />
            )}
          </Field>
        </form>
      </Modal>
    </DialogContext.Provider>
  );
}

export function useDialogs(): DialogContextValue {
  const context = useContext(DialogContext);
  if (!context) {
    throw new Error('useDialogs must be used inside DialogProvider');
  }
  return context;
}
